import { type BasketLine } from "@/lib/basket";
import { originFirst } from "@/lib/reviewLocale";
import { getPostHogHeaders } from "@/lib/posthog";
import { isCardSku } from "@/lib/cards";
import { cardOfferFor } from "@/lib/cardOffer";

export type JobStage =
  | "uploaded"
  | "analyzing"
  | "rendering"
  | "preparing_preview"
  | "completed"
  | "failed";

export type PreviewStyle = {
  slug: string;
  name: string;
  imageUrl: string;
  description?: string;
};

export type JobStatus = {
  jobId: string;
  status: "queued" | "processing" | "completed" | "failed";
  stage: JobStage;
  progress?: number;
  previewReady?: boolean;
  previewUrl?: string;
  styles?: PreviewStyle[];
  error?: string;
  themeSlug?: string | null;
  paletteWarmth?: "warm" | "neutral" | "cool" | null;
  lightingMood?: "sunny" | "golden" | "misty" | "overcast" | "dusk" | null;
  realismLevel?: "realistic" | "stylized" | "graphic" | null;
  /** The pack the job is on. Authoritative — prefer it over the remembered sessionStorage value. */
  styleSlug?: string | null;
  motoShotSlug?: "viewpoint" | "riding" | "kickstand" | null;
  helmetModeSlug?: "off" | "visor-up" | "visor-down" | null;
  hasRiderPhoto?: boolean;
  /** Pets line: the animals in the order they were added, and the answers given so far. */
  pets?: PetInfo[] | null;
  petPoseSlug?: PetPose | null;
  petOptions?: PetOptions | null;
  petBossIndex?: number | null;
  posterTextMode?: string | null;
  posterText?: string | null;
  customSceneDescription?: string | null;
  /** Family card: the house was uploaded, so the scene step offers "our home". */
  hasHomePhoto?: boolean;
};

export type PetSpecies = "dog" | "cat" | "rabbit" | "horse" | "other";
export type PetPose = "together" | "playing" | "sleeping";
/** The Old Master's answers, question key → option slug. A question with no answer paints its first option. */
export type PetOptions = Partial<Record<OldMasterQuestionKey, string>>;
export type OldMasterQuestionKey = "era" | "dress" | "setting" | "backdrop" | "prop" | "expression";
export type PetInfo = { name: string; species: PetSpecies; photoUrl?: string | null };

export type PreviewData = {
  jobId: string;
  watermarkedImageUrl: string;
  styleSlug: string;
  orientation?: "Portrait" | "Landscape" | string;
  title?: string;
  styles?: PreviewStyle[];
  revisionCount?: number;
  maxRevisions?: number;
  themeSlug?: string | null;
  customSceneDescription?: string | null;
  posterTextMode?: "none" | "location" | "custom" | "signature" | "names" | null;
  posterText?: string | null;
  paletteWarmth?: "warm" | "neutral" | "cool" | null;
  lightingMood?: "sunny" | "golden" | "misty" | "overcast" | "dusk" | null;
  realismLevel?: "realistic" | "stylized" | "graphic" | null;
  licensePlateMode?: "none" | "custom" | null;
  licensePlateText?: string | null;
  licensePlateStyle?: "us" | "euro" | "vintage" | null;
  occupantMode?: "unoccupied" | "driver" | "both" | null;
  occupantStyle?: "gouache" | "silhouette" | "detailed" | null;
  pets?: PetInfo[] | null;
  petPoseSlug?: PetPose | null;
  petOptions?: PetOptions | null;
  petBossIndex?: number | null;
  person1Url?: string | null;
  person2Url?: string | null;
  // Which version of the artwork this preview is showing. Lower than revisionCount when the
  // customer has rolled back to an older one.
  currentRevisionNumber?: number | null;
  // Identified make+model ("Porsche 911"). Null on jobs rendered before the processor started
  // naming cars, and whenever the model wasn't confident — callers fall back to generic copy.
  carName?: string | null;
  // The paid order for this poster, if any — the checkout page redirects to it when the cart is empty
  // (back after paying) rather than let the same print be bought twice.
  paidOrderId?: string | null;
  // A design made for Christmas cards (the card builder's jobs): its preview opens on the card editor.
  isCard?: boolean;
  // Family card only: who is on it, with their photos, for the photo revision.
  family?: FamilyCastData | null;
};

export type FamilyCastData = {
  group: { photoUrl: string; count: number } | null;
  people: { photoUrl: string }[];
  pets: { species: PetSpecies; photoUrl: string }[];
  maxPeople: number;
  maxPets: number;
};

export type PublicReview = {
  // The review's own id. The homepage curation (lib/proofQuotes, lib/curatedPhotos) names reviews
  // by this, so a hand-picked quote survives the customer editing their name or car description.
  id: string;
  carDescription: string;
  firstName?: string | null;
  city?: string | null;
  // US state code, set for US customers only.
  state?: string | null;
  rating: number;
  reviewText?: string | null;
  originalLanguage: "nl" | "de" | string;
  displayLanguage: "nl" | "de" | string;
  isAutoTranslated: boolean;
  mediaUrls: string[];
  createdAt: string;
  isStarred: boolean;
  // Buying motives this review speaks to (see @/lib/reviewAngles). Empty for generic praise, and
  // for any review not yet tagged by tools/reviewangles/classify.py.
  angles?: string[];
  // Our public answer, already in the reader's language. Null when we have not answered.
  replyText?: string | null;
  repliedAt?: string | null;
  isReplyAutoTranslated?: boolean;
};

export type OrderItemInput = {
  sku: string;
  productName: string;
  price: number;
  quantity?: number;
  attributes?: Record<string, string>;
};

export type CheckoutRequest = {
  imageJobId: string;
  email: string;
  items: OrderItemInput[];
  /** Express checkout: the wallet supplies name/email/address at confirm time. */
  express?: boolean;
  name?: string;
  street?: string;
  houseNumber?: string;
  houseNumberAddition?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  country?: string;
  discountCode?: string;
  priorityShipping?: boolean;
  fbc?: string | null;
  fbp?: string | null;
  externalId?: string | null;
  acquisitionAngle?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

export type OrderItemResponse = {
  sku: string;
  productName: string;
  price: number;
  quantity: number;
  attributes?: Record<string, string>;
};

export type TrackingCheckpoint = {
  checkpointTime?: string | null;
  message?: string | null;
  location?: string | null;
  tag?: string | null;
  subtag?: string | null;
};

// The proof step. Present on every paid order placed since artwork approval shipped; absent for
// unpaid, cancelled, and pre-feature ("legacy") orders, which never had one.
export type ArtworkApproval = {
  // "awaiting" — waiting on the customer. "changeRequested" — waiting on us, clock stopped.
  // "approved" — released to print. Computed server-side so the client can never disagree with the
  // backend about whether an approve button should exist.
  state: "awaiting" | "changeRequested" | "approved";
  // Full size, no watermark. The file they are being asked to judge.
  artworkUrl?: string | null;
  readyAt?: string | null;
  // When it approves itself. Null while a change request is open — that is the paused clock.
  autoApproveAt?: string | null;
  approvedAt?: string | null;
  approvedBy?: "customer" | "auto" | "staff" | "legacy" | null;
  changeRequestCount: number;
  requests: { message: string; createdAt: string; resolvedAt?: string | null }[];
  // Anything but "Completed" means a render the customer asked for is in flight.
  jobStatus: "Completed" | "Pending" | "Processing" | "Failed";
  revisionsUsed: number;
  maxRevisions: number;
  // Whether they can queue another render right now. Server-computed for the same reason `state` is.
  canEdit: boolean;
  // True while the high-resolution upscale is being generated for customer inspection.
  isUpscaling?: boolean;
  upscaleError?: string | null;
};

export type OrderResponse = {
  id: string;
  orderNumber: string;
  email: string;
  name?: string;
  street?: string;
  houseNumber?: string;
  houseNumberAddition?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  // 2-letter shipping country the order was placed with. Decides which address form the order page
  // puts up (NL wants a house number, US wants a state). Not editable after payment.
  country?: string;
  // The delivery address may still be corrected from the order page — true until the print reaches
  // the printer. Computed server-side so the button is never offered on a parcel that already exists.
  canEditAddress?: boolean;
  imageJobId?: string;
  status: string;
  totalAmount: number;
  currency: string;
  items: OrderItemResponse[];
  downloadUrl?: string;
  downloadUrlStandard?: string;
  trackingUrl?: string;
  trackingNumber?: string;
  carrier?: string;
  trackingStatus?: string;
  trackingExpectedDelivery?: string;
  trackingCheckpoints?: TrackingCheckpoint[];
  deliveredAt?: string;
  createdAt?: string;
  paidAt?: string;
  // The basket this order belongs to (absent for standalone orders). Used to key the single Pixel
  // Purchase event so a multi-print basket dedupes to one Meta conversion.
  basketId?: string;
  // Present when this order is part of a basket: every order in the basket (including this one).
  basketOrders?: OrderResponse[];
  artwork?: ArtworkApproval | null;
  sizeUpgrade?: SizeUpgradeOffer | null;
  // The customer's own referral link, on a paid US order once the code exists. See ReferralShare.
  referral?: ReferralShareInfo | null;
  // The Christmas cards offer: this order's design as a card pack (backend CardOffers.IsEligible —
  // a paid US poster order, cards open, no card pack on the design or in the basket yet).
  cardOffer?: CardOfferInfo | null;
};

export type CardOfferInfo = {
  jobId: string;
  /** The design's 1K render, for the small card picture. */
  artUrl?: string | null;
  landscape: boolean;
};

export type ReferralShareInfo = {
  code: string;
  url: string;
  shareTextShort: string;
  shareTextGroup: string;
  /** Referral credit this customer has, in USD. Applies itself at their next checkout. */
  creditUsd: number;
};

// The post-purchase size-upgrade offer (A/B tested on the order page): same product family, largest
// sheet, 20% off the price difference. Absent when the order does not qualify or the window closed.
export type SizeUpgradeOffer = {
  sku: string;
  productName: string;
  sizeCm?: string | null;
  // The same jump at list price, for the strike-through.
  listDelta: number;
  // What the customer actually pays (extra), in the order's own currency.
  price: number;
  currency: string;
  // "available" — may be bought. "pending" — a payment link exists, unpaid. "paid" — done.
  state: "available" | "pending" | "paid";
  // Only while pending: the link that finishes the payment.
  paymentUrl?: string | null;
  // Target print area over current print area, one decimal (30x40 -> 50x70 = 2.9). The card sells
  // this number, not the euro difference.
  areaFactor: number;
};

export const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, "") ||
  (process.env.NODE_ENV === "production"
    ? "https://api.vellumprintworks.com"
    : "http://localhost:5265");

// Error bodies come back as JSON like {"error":"Maximum revisions reached"} (or ASP.NET
// ProblemDetails). Pages show err.message directly to the user, so unwrap it into plain
// text instead of leaking raw JSON/HTML into the UI.
function extractErrorMessage(raw: string, status: number): string {
  const fallback = `Request failed with status ${status}`;
  const text = raw.trim();
  if (!text) return fallback;
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      const obj = parsed as Record<string, unknown>;
      for (const key of ["error", "message", "detail", "title"]) {
        const value = obj[key];
        if (typeof value === "string" && value.trim()) return value.trim();
      }
      return fallback;
    }
    if (typeof parsed === "string" && parsed.trim()) return parsed.trim();
  } catch {
    // Not JSON — fall through to the raw text checks below.
  }
  if (text.startsWith("<")) return fallback; // HTML error page
  return text.length > 300 ? fallback : text;
}

// The backend's machine-readable error code (the `error` field, e.g. "rate_limit_exceeded"),
// so pages can map known failures to localized copy instead of showing the raw message.
function extractErrorCode(raw: string): string | null {
  try {
    const parsed: unknown = JSON.parse(raw.trim());
    if (parsed && typeof parsed === "object") {
      const value = (parsed as Record<string, unknown>).error;
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  } catch {
    // Not JSON — no code to extract.
  }
  return null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const isFormData = init?.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      Accept: "application/json",
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...getPostHogHeaders(),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(extractErrorMessage(body, response.status), response.status, extractErrorCode(body));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }
  return (await response.text()) as T;
}

export async function uploadImage(
  file: File,
  styleSlug?: string,
  attribution?: {
    acquisitionAngle?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  },
  locale?: string,
): Promise<{ jobId: string }> {
  const formData = new FormData();
  formData.append("file", file);
  if (styleSlug) formData.append("styleSlug", styleSlug);
  if (attribution?.acquisitionAngle) formData.append("acquisitionAngle", attribution.acquisitionAngle);
  if (attribution?.utmSource) formData.append("utmSource", attribution.utmSource);
  if (attribution?.utmMedium) formData.append("utmMedium", attribution.utmMedium);
  if (attribution?.utmCampaign) formData.append("utmCampaign", attribution.utmCampaign);
  if (locale) formData.append("locale", locale);
  return request<{ jobId: string }>("/api/upload", {
    method: "POST",
    body: formData,
  });
}

export async function uploadDescription(
  carDescription: string,
  styleSlug?: string,
  attribution?: {
    acquisitionAngle?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
  },
  locale?: string,
): Promise<{ jobId: string }> {
  return request<{ jobId: string }>("/api/upload/description", {
    method: "POST",
    body: JSON.stringify({
      CarDescription: carDescription,
      StyleSlug: styleSlug ?? null,
      AcquisitionAngle: attribution?.acquisitionAngle ?? null,
      UtmSource: attribution?.utmSource ?? null,
      UtmMedium: attribution?.utmMedium ?? null,
      UtmCampaign: attribution?.utmCampaign ?? null,
      Locale: locale ?? null,
    }),
  });
}

export async function setStyle(jobId: string, styleSlug: string): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/style`, {
    method: "POST",
    body: JSON.stringify({ StyleSlug: styleSlug }),
  });
}

export async function setTheme(
  jobId: string,
  themeSlug: string,
  customSceneDescription?: string,
  mood?: { paletteWarmth?: string | null; lightingMood?: string | null },
): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/theme`, {
    method: "POST",
    body: JSON.stringify({
      ThemeSlug: themeSlug,
      CustomSceneDescription: customSceneDescription ?? null,
      PaletteWarmth: mood?.paletteWarmth ?? null,
      LightingMood: mood?.lightingMood ?? null,
    }),
  });
}

export type SceneSuggestion = { isLocation: boolean; description: string | null };

/**
 * A location search that matched no tile. The server answers whether the term names a real place
 * and, when it does, hands back a scene description to drop into the custom-scene field. Every
 * failure is a `false` rather than a throw: a missing suggestion must never look like a broken step.
 */
export async function suggestScene(
  jobId: string,
  query: string,
  locale: string,
  signal?: AbortSignal,
): Promise<SceneSuggestion> {
  return request<SceneSuggestion>(`/api/jobs/${jobId}/scene-suggestion`, {
    method: "POST",
    body: JSON.stringify({ Query: query, Locale: locale }),
    signal,
  });
}

export type TileBeta = { alpha: number; beta: number };

/** The /theme picker's tile posteriors for the visitor's market (backend Services/TileBandit.cs). */
export type TileBanditParams = {
  stylePrior: TileBeta;
  scenePrior: TileBeta;
  styles: Record<string, TileBeta>;
  scenes: Record<string, TileBeta>;
};

export async function getTileBandit(locale: string, signal?: AbortSignal): Promise<TileBanditParams> {
  return request<TileBanditParams>(`/api/tile-bandit?locale=${encodeURIComponent(locale)}`, { signal });
}

export async function setMood(
  jobId: string,
  mood: { paletteWarmth?: string | null; lightingMood?: string | null },
): Promise<void> {
  const body: Record<string, string | null> = {};
  if (mood.paletteWarmth !== undefined) body.PaletteWarmth = mood.paletteWarmth ?? "";
  if (mood.lightingMood !== undefined) body.LightingMood = mood.lightingMood ?? "";
  return request<void>(`/api/jobs/${jobId}/mood`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Car state on the strip (drag-strip theme only). "" clears back to the default racing shot.
/** What the machine is doing: "viewpoint" | "riding" | "kickstand". Empty string clears. */
export async function setMotoShot(jobId: string, motoShotSlug: string): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/moto-shot`, {
    method: "POST",
    body: JSON.stringify({ MotoShotSlug: motoShotSlug }),
  });
}

/** How much of the rider's face shows: "off" | "visor-up" | "visor-down". Empty string clears. */
export async function setHelmet(jobId: string, helmetModeSlug: string): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/helmet`, {
    method: "POST",
    body: JSON.stringify({ HelmetModeSlug: helmetModeSlug }),
  });
}

export async function setCarState(jobId: string, carStateSlug: string): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/car-state`, {
    method: "POST",
    body: JSON.stringify({ CarStateSlug: carStateSlug }),
  });
}

// Realism/look dial (vintage-poster only). "" clears back to the default (realistic).
export async function setRealism(
  jobId: string,
  realismLevel: "realistic" | "stylized" | "graphic" | "",
): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/realism`, {
    method: "POST",
    body: JSON.stringify({ RealismLevel: realismLevel }),
  });
}

export async function setOrientation(jobId: string, orientation: "portrait" | "landscape"): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/orientation`, {
    method: "POST",
    body: JSON.stringify({ Orientation: orientation }),
  });
}

export async function setPosterText(
  jobId: string,
  mode: "none" | "location" | "custom" | "signature" | "names",
  text?: string,
): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/poster-text`, {
    method: "POST",
    body: JSON.stringify({
      Mode: mode,
      Text: text ?? null,
    }),
  });
}

// One person slot's intent for setPeople: a File replaces the slot, "clear" empties it,
// "keep" leaves whatever is already there untouched (merge semantics, so an existing person
// is never silently dropped).
export type PersonSlot = File | "keep" | "clear";

// Set up to two reference photos of people to paint into the poster (vintage-poster).
// Pass two slots, one per person. Example: [file, "keep"] replaces person 1 and keeps person 2.
export async function setPeople(jobId: string, slots: PersonSlot[]): Promise<void> {
  const formData = new FormData();
  slots.forEach((slot, i) => {
    const n = i + 1;
    if (slot instanceof File) formData.append(`person${n}`, slot);
    else if (slot === "clear") formData.append(`clearPerson${n}`, "true");
    // "keep" -> append nothing for this slot.
  });
  return request<void>(`/api/jobs/${jobId}/people`, {
    method: "POST",
    body: formData,
  });
}

/**
 * The animals on a pet design. The first animal's photo is the upload itself; the others travel
 * here. Every animal's name and species is sent every time, in order: the list is replaced whole.
 */
export async function setPets(
  jobId: string,
  pets: { name: string; species: PetSpecies }[],
  extraPhotos: File[],
): Promise<void> {
  const formData = new FormData();
  pets.forEach((pet) => {
    formData.append("names", pet.name);
    formData.append("species", pet.species);
  });
  extraPhotos.forEach((file, i) => formData.append(`pet${i + 2}`, file));
  return request<void>(`/api/jobs/${jobId}/pets`, {
    method: "POST",
    body: formData,
  });
}

/** The pet answers: pose, the Old Master's questions, and the head of the household. */
export async function setPetBrief(
  jobId: string,
  brief: { poseSlug?: PetPose; options?: PetOptions; bossIndex?: number | null },
): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/pet-brief`, {
    method: "POST",
    body: JSON.stringify({
      PoseSlug: brief.poseSlug ?? null,
      Options: brief.options ?? null,
      BossIndex: brief.bossIndex ?? null,
      ClearBoss: brief.bossIndex === null,
    }),
  });
}

export async function getJobStatus(jobId: string): Promise<JobStatus> {
  return request<JobStatus>(`/api/jobs/${jobId}`);
}

export async function getPreview(
  jobId: string,
  marketingCookies?: { fbc?: string | null; fbp?: string | null; externalId?: string | null },
  locale?: string,
  // Set for refetches within a page the visitor is already on (e.g. after rolling back to an
  // older version) so they don't each count as another Meta ViewContent conversion.
  skipTracking?: boolean,
): Promise<PreviewData> {
  const params = new URLSearchParams();
  if (marketingCookies?.fbc) params.set("fbc", marketingCookies.fbc);
  if (marketingCookies?.fbp) params.set("fbp", marketingCookies.fbp);
  if (marketingCookies?.externalId) params.set("externalId", marketingCookies.externalId);
  if (locale) params.set("locale", locale);
  if (skipTracking) params.set("skipTracking", "true");
  const query = params.toString();
  return request<PreviewData>(`/api/preview/${jobId}${query ? `?${query}` : ""}`);
}

// Fires the server-side Meta AddToCart conversion (deduplicated against the client Pixel via
// event_id "cart_{jobId}"). Best-effort — never blocks the add-to-cart UX.
export async function trackAddToCartEvent(
  jobId: string,
  data: { sku: string; productName?: string; locale?: string },
  marketingCookies?: { fbc?: string | null; fbp?: string | null; externalId?: string | null },
): Promise<void> {
  try {
    await request<void>(`/api/preview/${jobId}/add-to-cart`, {
      method: "POST",
      body: JSON.stringify({
        Sku: data.sku,
        ProductName: data.productName ?? null,
        Locale: data.locale ?? null,
        Fbc: marketingCookies?.fbc ?? null,
        Fbp: marketingCookies?.fbp ?? null,
        ExternalId: marketingCookies?.externalId ?? null,
      }),
    });
  } catch {
    // Tracking is best-effort; swallow failures.
  }
}

export async function getReviews(locale: string, starred?: boolean): Promise<PublicReview[]> {
  const params = new URLSearchParams({ locale });
  if (starred !== undefined) params.set("starred", String(starred));
  // Ordered here rather than per component, so every surface that shows proof — the wall, the
  // carousel, the buy page, checkout, /reviews — leads with the reviews written in the reader's own
  // market. See lib/reviewLocale.
  return originFirst(await request<PublicReview[]>(`/api/reviews?${params.toString()}`), locale);
}

// Cached variant of getReviews for the marketing surfaces that don't need to be real-time — the
// aggregate-rating banner, the gallery, the proof wall, the JSON-LD. Same URL and options from
// every caller on purpose: Next memoises identical fetches within one render, so the homepage
// makes one reviews request per page view, served from the edge data cache between revalidations.
// The origin sits in Amsterdam and the visitors in the US, so every fetch skipped is ~150 ms.
// A day between revalidations: a stale entry is served at once and refreshed in the background, so
// only an empty cache (a fresh deploy, an eviction) ever makes a visitor wait on the origin.
export async function getReviewsCached(locale: string, ttlSeconds = 86_400): Promise<PublicReview[]> {
  const response = await fetch(`${API_BASE_URL}/api/reviews?locale=${encodeURIComponent(locale)}`, {
    headers: { Accept: "application/json" },
    next: { revalidate: ttlSeconds },
  });
  if (!response.ok) {
    throw new Error(`Reviews request failed with status ${response.status}`);
  }
  return originFirst((await response.json()) as PublicReview[], locale);
}

export type ReviewStats = { average: number; count: number };

/**
 * The last-known site-wide rating (prod DB, 2026-10-01: 202 approved reviews, mean 4.87), rendered
 * whenever the real figure is not back within REVIEW_STATS_DEADLINE_MS. It prints the same
 * "4.9 · 202" line as the live value, so the fallback and the real render are the same width and
 * nothing re-wraps. Refresh it when the count gains a digit.
 */
export const LAST_KNOWN_REVIEW_STATS: ReviewStats = { average: 4.87, count: 202 };

/** How long the shell waits for the reviews before it renders the last-known rating instead. */
const REVIEW_STATS_DEADLINE_MS = 300;

// Site-wide aggregate rating, computed from the real approved reviews. The landers render it in the
// shell, so on a cold cache it would hold the first byte for a full origin round trip: the fetch is
// raced against a short deadline and loses to the last-known figure. It keeps running — the
// gallery and proof wall below stream the same memoised request — and fills the cache for the next
// visitor. A failed or empty fetch also falls back, so the line always renders.
export async function getReviewStats(locale: string): Promise<ReviewStats> {
  const live = getReviewsCached(locale).then(
    (reviews): ReviewStats =>
      reviews.length
        ? { average: reviews.reduce((total, review) => total + review.rating, 0) / reviews.length, count: reviews.length }
        : LAST_KNOWN_REVIEW_STATS,
    () => LAST_KNOWN_REVIEW_STATS,
  );
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<ReviewStats>((resolve) => {
    timer = setTimeout(() => resolve(LAST_KNOWN_REVIEW_STATS), REVIEW_STATS_DEADLINE_MS);
  });
  try {
    return await Promise.race([live, deadline]);
  } finally {
    clearTimeout(timer);
  }
}

// One canonical, locale-aware formatting of a star rating (e.g. 4.96 -> "5.0" /
// "5,0"), matching what the /reviews page and review carousel already show.
export function formatRating(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

export type RevisionPayload = {
  editInstructions?: string;
  styleSlug?: string;
  themeSlug?: string;
  customSceneDescription?: string;
  posterTextMode?: "none" | "location" | "custom" | "signature" | "names";
  posterText?: string;
  petPoseSlug?: PetPose;
  petOptions?: PetOptions;
  paletteWarmth?: "warm" | "neutral" | "cool" | "";
  lightingMood?: "sunny" | "golden" | "misty" | "overcast" | "dusk" | "";
  realismLevel?: "realistic" | "stylized" | "graphic" | "";
  orientation?: "portrait" | "landscape";
  peopleChanged?: boolean;
  licensePlateMode?: "none" | "custom";
  licensePlateText?: string;
  licensePlateStyle?: "us" | "euro" | "vintage";
  occupantMode?: "unoccupied" | "driver" | "both";
  occupantStyle?: "gouache" | "silhouette" | "detailed";
};

export async function requestRevision(jobId: string, payload: string | RevisionPayload): Promise<void> {
  const body = typeof payload === "string" ? { editInstructions: payload } : payload;
  return request<void>(`/api/jobs/${jobId}/revision`, {
    method: "POST",
    body: JSON.stringify({
      EditInstructions: body.editInstructions ?? null,
      StyleSlug: body.styleSlug ?? null,
      ThemeSlug: body.themeSlug ?? null,
      CustomSceneDescription: body.customSceneDescription ?? null,
      PosterTextMode: body.posterTextMode ?? null,
      PosterText: body.posterText ?? null,
      PaletteWarmth: body.paletteWarmth ?? null,
      LightingMood: body.lightingMood ?? null,
      RealismLevel: body.realismLevel ?? null,
      Orientation: body.orientation ?? null,
      PeopleChanged: body.peopleChanged ?? false,
      LicensePlateMode: body.licensePlateMode ?? null,
      LicensePlateText: body.licensePlateText ?? null,
      LicensePlateStyle: body.licensePlateStyle ?? null,
      OccupantMode: body.occupantMode ?? null,
      OccupantStyle: body.occupantStyle ?? null,
      PetPoseSlug: body.petPoseSlug ?? null,
      PetOptions: body.petOptions ?? null,
    }),
  });
}

// One finished render in a job's version history. `position` is the place in the chain (0 = the
// first generation, then 1, 2, 3…) and is what the UI labels versions with; `revisionNumber` is
// the backend's render counter, which skips numbers once a rollback discarded a branch.
export type JobRevision = {
  revisionNumber: number;
  position: number;
  imageUrl?: string | null;
  editInstruction?: string | null;
  createdAt: string;
  isCurrent: boolean;
  styleSlug: string;
  themeSlug?: string | null;
  orientation: string;
};

export type JobRevisionHistory = {
  jobId: string;
  revisions: JobRevision[];
  currentRevisionNumber?: number | null;
  revisionsUsed: number;
  maxRevisions: number;
  canRevert: boolean;
};

export async function getJobRevisions(jobId: string): Promise<JobRevisionHistory> {
  return request<JobRevisionHistory>(`/api/jobs/${jobId}/revisions`);
}

// Rolls the artwork back to an earlier version. Free — nothing is regenerated and no revision is
// spent. The newer versions stay listed until a fresh revision is made from this one.
export async function revertToRevision(jobId: string, revisionNumber: number): Promise<JobRevisionHistory> {
  return request<JobRevisionHistory>(`/api/jobs/${jobId}/revisions/${revisionNumber}/revert`, {
    method: "POST",
  });
}

export type JobSummary = {
  jobId: string;
  status: "pending" | "processing" | "completed" | "failed" | "awaitingStyle" | "awaitingOrientation";
  styleSlug: string;
  orientation: string;
  imageUrl?: string | null;
  carName?: string | null;
  createdAt: string;
  completedAt?: string | null;
  revisionCount: number;
  maxRevisions: number;
  ordered: boolean;
  hidden: boolean;
  /** Made for cards: its preview opens on the card editor already. */
  isCard?: boolean;
  /** A card pack of this design has been paid for. */
  cardsOrdered?: boolean;
};

// Batch status for the job ids this browser remembers (see lib/designs.ts). Unknown/expired ids
// are simply absent from the response.
export async function getJobSummaries(jobIds: string[]): Promise<JobSummary[]> {
  if (jobIds.length === 0) return [];
  return request<JobSummary[]>(`/api/jobs/summary?ids=${encodeURIComponent(jobIds.join(","))}`);
}

export async function upsertCheckoutIntent(
  jobId: string,
  email: string,
  productSku?: string | null,
  firstName?: string | null,
  basket?: BasketLine[],
): Promise<void> {
  try {
    await request<void>("/api/checkout-intent", {
      method: "POST",
      body: JSON.stringify({
        JobId: jobId,
        Email: email,
        ProductSku: productSku ?? null,
        FirstName: firstName ?? null,
        Basket: basket ?? null,
      }),
    });
  } catch {
    // Best-effort; don't block typing if it fails.
  }
}

/**
 * The save-design card (preview-save-design-v1): the server mails this design to the address now and
 * follows up twice. Throws on a failed request, so the card can say it did not go through.
 */
export async function saveDesign(jobId: string, email: string): Promise<void> {
  await request<void>("/api/saved-design", {
    method: "POST",
    body: JSON.stringify({ JobId: jobId, Email: email }),
  });
}

export type CheckoutIntentData = { basket: BasketLine[]; discountCode: string | null };

/**
 * The basket and personal discount code the shopper left behind, as the server saw them. A
 * recovery link opened in another browser has neither in local storage, so this is what puts the
 * checkout back the way they left it. Absent intent (or no basket saved) resolves to null.
 */
export async function getCheckoutIntent(jobId: string): Promise<CheckoutIntentData | null> {
  try {
    return await request<CheckoutIntentData>(`/api/checkout-intent/${jobId}`);
  } catch {
    return null;
  }
}

// The Christmas cards offer surface behind a card line of this design (lib/cardOffer.ts), so the card
// order records what sold it. Read here, where every checkout path builds its request, rather than
// threaded through each of them. A poster line never carries one.
function cardOfferOf(jobId: string, items: OrderItemInput[]): string | undefined {
  return items.some((i) => isCardSku(i.sku)) ? cardOfferFor(jobId) : undefined;
}

export async function createOrder(data: CheckoutRequest): Promise<OrderResponse> {
  return request<OrderResponse>("/api/orders", {
    method: "POST",
    body: JSON.stringify({
      ImageJobId: data.imageJobId,
      CardOffer: cardOfferOf(data.imageJobId, data.items),
      Email: data.email,
      Items: data.items.map((i) => ({
        Sku: i.sku,
        ProductName: i.productName,
        Price: i.price,
        Quantity: i.quantity ?? 1,
        Attributes: i.attributes ?? null,
      })),
      Name: data.name,
      Street: data.street,
      HouseNumber: data.houseNumber,
      HouseNumberAddition: data.houseNumberAddition,
      PostalCode: data.postalCode,
      City: data.city,
      State: data.state,
      Country: data.country ?? "NL",
      Express: data.express ?? false,
      DiscountCode: data.discountCode,
      PriorityShipping: data.priorityShipping ?? false,
      Fbc: data.fbc,
      Fbp: data.fbp,
      ExternalId: data.externalId,
      AcquisitionAngle: data.acquisitionAngle,
      UtmSource: data.utmSource,
      UtmMedium: data.utmMedium,
      UtmCampaign: data.utmCampaign,
    }),
  });
}

export async function startPayment(
  orderId: string,
  method: string = "card",
): Promise<{ checkoutUrl: string }> {
  return request<{ checkoutUrl: string }>(`/api/checkout/${orderId}`, {
    method: "POST",
    body: JSON.stringify({ Method: method }),
  });
}

// One print in a basket: a finished image plus the product(s) chosen for it.
export type BasketLineInput = {
  jobId: string;
  items: OrderItemInput[];
};

export type BasketOrdersResponse = {
  basketId: string;
  orders: OrderResponse[];
  totalAmount: number;
};

export type CreateBasketRequest = {
  lines: BasketLineInput[];
  email: string;
  /** Express checkout: the wallet supplies name/email/address at confirm time. */
  express?: boolean;
  name?: string;
  street?: string;
  houseNumber?: string;
  houseNumberAddition?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  country?: string;
  discountCode?: string;
  priorityShipping?: boolean;
  fbc?: string | null;
  fbp?: string | null;
  externalId?: string | null;
  acquisitionAngle?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

// Creates one Order per line (1 print = 1 order), all sharing a BasketId for a single combined payment.
export async function createBasketOrders(data: CreateBasketRequest): Promise<BasketOrdersResponse> {
  return request<BasketOrdersResponse>("/api/orders/basket", {
    method: "POST",
    body: JSON.stringify({
      Lines: data.lines.map((l) => ({
        ImageJobId: l.jobId,
        CardOffer: cardOfferOf(l.jobId, l.items),
        Items: l.items.map((i) => ({
          Sku: i.sku,
          ProductName: i.productName,
          Price: i.price,
          Quantity: i.quantity ?? 1,
          Attributes: i.attributes ?? null,
        })),
      })),
      Email: data.email,
      Name: data.name,
      Street: data.street,
      HouseNumber: data.houseNumber,
      HouseNumberAddition: data.houseNumberAddition,
      PostalCode: data.postalCode,
      City: data.city,
      State: data.state,
      Country: data.country ?? "NL",
      Express: data.express ?? false,
      DiscountCode: data.discountCode,
      PriorityShipping: data.priorityShipping ?? false,
      Fbc: data.fbc,
      Fbp: data.fbp,
      ExternalId: data.externalId,
      AcquisitionAngle: data.acquisitionAngle,
      UtmSource: data.utmSource,
      UtmMedium: data.utmMedium,
      UtmCampaign: data.utmCampaign,
    }),
  });
}

// Pays for a whole basket (all orders sharing the BasketId) in one Stripe session.
export async function startBasketPayment(
  basketId: string,
  method: string = "card",
): Promise<{ checkoutUrl: string }> {
  return request<{ checkoutUrl: string }>(`/api/checkout/basket/${basketId}`, {
    method: "POST",
    body: JSON.stringify({ Method: method }),
  });
}

export async function getOrder(orderId: string): Promise<OrderResponse> {
  return request<OrderResponse>(`/api/orders/${orderId}`);
}

// "Yes, print this." Returns the order already showing as approved: the backend answers optimistically
// so the page flips on the first response, while the upscale and the Prodigi submission run behind it.
export async function approveArtwork(orderId: string): Promise<OrderResponse> {
  return request<OrderResponse>(`/api/orders/${orderId}/artwork/approve`, { method: "POST" });
}

export type ShippingAddressUpdate = {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberAddition: string;
  postalCode: string;
  city: string;
  state: string;
};

// "Send it here instead." Rewrites the delivery address while the print is still ours to redirect.
// For a basket the server writes it to every print at once — one parcel, one address.
export async function updateShippingAddress(
  orderId: string,
  address: ShippingAddressUpdate,
): Promise<OrderResponse> {
  return request<OrderResponse>(`/api/orders/${orderId}/address`, {
    method: "PUT",
    body: JSON.stringify({
      Name: address.name,
      Street: address.street,
      HouseNumber: address.houseNumber,
      HouseNumberAddition: address.houseNumberAddition,
      PostalCode: address.postalCode,
      City: address.city,
      State: address.state,
    }),
  });
}

// "Yes, make it bigger." Mints the Stripe payment link for the size-upgrade offer; the SKU swap
// happens server-side once the link is paid. Idempotent: a second call returns the same link.
export async function createSizeUpgrade(orderId: string): Promise<{ paymentUrl: string }> {
  return request<{ paymentUrl: string }>(`/api/orders/${orderId}/upgrade`, { method: "POST" });
}

// "Not yet — change this." Stops the auto-approval clock and opens a thread on our side.
export async function requestArtworkChange(orderId: string, message: string): Promise<OrderResponse> {
  return request<OrderResponse>(`/api/orders/${orderId}/artwork/changes`, {
    method: "POST",
    body: JSON.stringify({ Message: message }),
  });
}

export type AddressLookupResult = {
  found: boolean;
  street?: string;
  city?: string;
  // US only: 2-letter state code (e.g. "CA").
  state?: string;
  error?: string;
};

export async function lookupAddress(
  postalCode: string,
  houseNumber: string,
  country: string = "NL",
  street?: string,
): Promise<AddressLookupResult> {
  const params = new URLSearchParams({ postalCode, houseNumber, country });
  if (street && street.trim()) params.set("street", street.trim());
  try {
    return await request<AddressLookupResult>(`/api/address/lookup?${params.toString()}`);
  } catch {
    return { found: false, error: "lookup_failed" };
  }
}

// Stripe publishable key, served by the backend so it can never drift from the account the secret key
// belongs to. See PaymentController.GetCheckoutConfig.
export async function getCheckoutConfig(): Promise<{ publishableKey: string }> {
  return request<{ publishableKey: string }>("/api/checkout/config");
}

export type ExpressCheckoutSession = { clientSecret: string | null; orderId: string };

// Opens a `custom` UI-mode Checkout Session for the Express Checkout Element. `checkout.session.completed`
// still fires on confirm, so the whole post-payment pipeline is the one the hosted checkout already uses.
export async function createExpressSession(target: { orderId?: string; basketId?: string }): Promise<ExpressCheckoutSession> {
  const path = target.basketId
    ? `/api/checkout/express/basket/${target.basketId}`
    : `/api/checkout/express/${target.orderId}`;
  return request<ExpressCheckoutSession>(path, { method: "POST" });
}

/** Rewrites the text a Christmas card pack prints, while its artwork still waits for approval. */
export async function updateCardText(orderId: string, attributes: Record<string, string>): Promise<OrderResponse> {
  return request<OrderResponse>(`/api/orders/${orderId}/card-text`, {
    method: "PUT",
    body: JSON.stringify(attributes),
  });
}

export type FamilyCardPerson = { file: File };
export type FamilyCardPet = { file: File; species: PetSpecies };

/**
 * Creates a family Christmas card job: every person, pet, the car and the house, each with its photo.
 * Sent over XMLHttpRequest rather than fetch, which cannot report upload progress: up to twelve
 * photos go in one request, and `onProgress` (0..1) drives the builder's percentage.
 */
export function uploadFamilyCard(
  cast: {
    people: FamilyCardPerson[];
    pets: FamilyCardPet[];
    car?: File | null;
    home?: File | null;
    /** One photo of several people instead of a photo per face, and how many are in it. */
    group?: { file: File; count: number } | null;
  },
  attribution?: { acquisitionAngle?: string; utmSource?: string; utmMedium?: string; utmCampaign?: string },
  locale?: string,
  onProgress?: (fraction: number) => void,
): Promise<{ jobId: string }> {
  const formData = new FormData();
  for (const person of cast.people) formData.append("peoplePhotos", person.file);
  for (const pet of cast.pets) {
    formData.append("petPhotos", pet.file);
    formData.append("petSpecies", pet.species);
  }
  if (cast.car) formData.append("carPhoto", cast.car);
  if (cast.home) formData.append("homePhoto", cast.home);
  if (cast.group) {
    formData.append("groupPhoto", cast.group.file);
    formData.append("groupCount", String(cast.group.count));
  }
  // The ad or page the shopper came in on. The family style is what makes this a cards job.
  if (attribution?.acquisitionAngle) formData.append("acquisitionAngle", attribution.acquisitionAngle);
  if (attribution?.utmSource) formData.append("utmSource", attribution.utmSource);
  if (attribution?.utmMedium) formData.append("utmMedium", attribution.utmMedium);
  if (attribution?.utmCampaign) formData.append("utmCampaign", attribution.utmCampaign);
  if (locale) formData.append("locale", locale);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/api/upload/card-family`);
    xhr.setRequestHeader("Accept", "application/json");
    for (const [name, value] of Object.entries(getPostHogHeaders())) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
    };
    xhr.onload = () => {
      const body = xhr.responseText ?? "";
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(extractErrorMessage(body, xhr.status), xhr.status, extractErrorCode(body)));
        return;
      }
      try {
        resolve(JSON.parse(body) as { jobId: string });
      } catch {
        reject(new ApiError(extractErrorMessage(body, xhr.status), xhr.status, null));
      }
    };
    xhr.onerror = () => reject(new ApiError("network_error", 0, "network_error"));
    xhr.ontimeout = () => reject(new ApiError("network_error", 0, "network_error"));
    xhr.send(formData);
  });
}

/** A family card photo revision: new photos for people, pets or the group photo already on it, and people or pets added. */
export type FamilyPhotoChange = {
  /** Slot → new photo. Slots: "group", "person:{index}", "pet:{index}" (zero-based, as in FamilyCastData). */
  replace: { slot: string; file: File }[];
  addPeople: { file: File }[];
  addPets: { file: File; species: PetSpecies }[];
};

/** Queues the photo revision: the card is edited with the new cast. Spends one revision. */
export async function reviseFamilyPhotos(jobId: string, change: FamilyPhotoChange): Promise<void> {
  const formData = new FormData();
  for (const r of change.replace) {
    formData.append("replaceSlots", r.slot);
    formData.append("replacePhotos", r.file);
  }
  for (const p of change.addPeople) formData.append("addPeoplePhotos", p.file);
  for (const p of change.addPets) {
    formData.append("addPetPhotos", p.file);
    formData.append("addPetSpecies", p.species);
  }
  return request<void>(`/api/jobs/${jobId}/card-family/photos`, { method: "POST", body: formData });
}

export async function setFamilyCardScene(jobId: string, scene: string, customScene?: string): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/card-family/scene`, {
    method: "POST",
    body: JSON.stringify({ scene, customScene: customScene ?? null }),
  });
}

export async function setFamilyCardGreeting(jobId: string, mode: "merry" | "custom" | "none", text?: string): Promise<void> {
  return request<void>(`/api/jobs/${jobId}/card-family/greeting`, {
    method: "POST",
    body: JSON.stringify({ mode, text: text ?? null }),
  });
}
